import { faCalendarDay, faChevronRight, faList } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ActivityType } from '@spike/activity-model';
import { Period } from '@spike/model';
import Pet from '@spike/pet-model';
import clsx from 'clsx';
import { ACTIVITY_FILTER_OPTIONS } from 'components/Activities/Icon/ActivityIcon';
import { ActivityTimeline } from 'components/Activities/Timeline/ActivityTimeline';
import { ActivityTimelineProvider, useActivityTimeline } from 'components/Activities/Timeline/ActivityTimelineProvider';
import styles from 'components/OrderDetail/Tabs/Activities/OrderDetailActivitiesTab.module.scss';
import SidePanel from 'components/UI/SidePanel';
import { DateRangePickerDropdown } from 'components/UI/V3/DateRangePickerDropdown';
import { SelectDropdown, SelectDropdownOption } from 'components/UI/V3/SelectDropdown/SelectDropdown';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';
import useTimeZone from 'hooks/useTimeZone';
import React from 'react';

const SECONDARY_PANEL_OFFSET = 590;

const ACTIVITY_TYPES: Array<SelectDropdownOption<ActivityType>> = ACTIVITY_FILTER_OPTIONS;

const useActivityHistoryDrawerStyles = makeStyles((theme: Theme) =>
    createStyles({
        drawerRoot: {
            'width': 'calc(100vw - 590px) !important',
            '& .MuiDrawer-paper .sidepanel-close-button': {
                right: `${SECONDARY_PANEL_OFFSET + 590 + 30}px`
            }
        },
        offsetPaper: {
            position: 'fixed',
            right: `${SECONDARY_PANEL_OFFSET}px !important`,
            [theme.breakpoints.down('md')]: {
                right: '0px !important',
                left: '0px !important'
            }
        },
        noShadow: {
            boxShadow: 'none !important'
        },
        borderSeparatorRight: {
            borderRight: '1px solid #D4D4D4'
        },
        drawerBackdrop: {
            width: 'calc(100vw - 590px) !important'
        }
    })
);

interface ActivityHistoryDrawerProps {
    open: boolean;
    onBack: () => void;
    pets: Array<Pet>;
    selectedPet?: Pet;
    customerId: number;
    dateLimits?: Period;
    zIndex?: number;
}

export const ActivityHistoryDrawer: React.FC<ActivityHistoryDrawerProps> = ({
    open,
    onBack,
    pets,
    selectedPet,
    customerId,
    dateLimits,
    zIndex
}) => {
    const classes = useActivityHistoryDrawerStyles();
    const scrollableContentRef = React.useRef<HTMLDivElement>(null);
    const activePet = selectedPet ?? pets[0];

    if (!open || !activePet) {
        return null;
    }

    return (
        <ActivityTimelineProvider
            pets={pets}
            pet={activePet}
            customerId={customerId}
            createActivityDateLimits={dateLimits}
            zIndex={zIndex}
        >
            <SidePanel
                side="right"
                hide={false}
                onClose={onBack}
                hideBackdrop={true}
                className={clsx(classes.drawerRoot)}
                classesInner={{
                    paper: clsx(classes.offsetPaper, classes.noShadow, classes.borderSeparatorRight)
                }}
                classesBackdrop={{
                    classes: {
                        root: classes.drawerBackdrop
                    }
                }}
                zIndex={zIndex ? zIndex + 1 : undefined}
            >
                <div className={styles.activityHistoryDrawerRoot}>
                    <div className={styles.activityHistoryHeader}>
                        <span className={styles.activityHistoryTitle}>Activity History</span>

                        <div className={styles.activityHistoryBackButtonContainer}>
                            <FontAwesomeIcon
                                icon={faChevronRight}
                                size="sm"
                                onClick={onBack}
                                className={styles.activityHistoryBackButton}
                            />
                        </div>
                    </div>

                    <div
                        ref={scrollableContentRef}
                        className={styles.activityHistoryContent}
                    >
                        <ActivityTimeline
                            readOnly={true}
                            infiniteScrollTrigger={scrollableContentRef.current}
                            classNames={{
                                timeline: styles.activityHistoryTimeline
                            }}
                        >
                            <ActivityHistoryFilters />
                        </ActivityTimeline>
                    </div>
                </div>
            </SidePanel>
        </ActivityTimelineProvider>
    );
};

const ActivityHistoryFilters: React.FC = () => {
    const timeZone = useTimeZone();
    const { todayOption, thisMonthOption, last30DaysOption, allTimeOption } = useDateRangeOptions();
    const { activityTypes, activityTypesChangeHandler, datePeriod, datePeriodChangeHandler } = useActivityTimeline();

    return (
        <div className={styles.activityHistoryFilters}>
            <SelectDropdown
                multiple
                showSelectAll
                label="Activities"
                options={ACTIVITY_TYPES}
                value={ACTIVITY_TYPES.filter(item => activityTypes.includes(item.id))}
                onChange={types => activityTypesChangeHandler(types.map(item => item.id))}
                buttonProps={{
                    size: 'large',
                    leftIcon: faList,
                    variant: 'outlined'
                }}
                dropdownProps={{
                    className: styles.activityHistoryDropdown,
                    mobileBottomModalProps: {
                        backdropClassName: styles.activityHistoryDropdownBackdrop
                    }
                }}
            />

            <DateRangePickerDropdown
                timeZone={timeZone}
                value={datePeriod}
                onChange={datePeriodChangeHandler}
                options={[todayOption, thisMonthOption, last30DaysOption, allTimeOption]}
                buttonProps={{
                    size: 'large',
                    leftIcon: faCalendarDay,
                    variant: 'outlined'
                }}
                dropdownProps={{
                    className: styles.activityHistoryDropdown,
                    mobileBottomModalProps: {
                        backdropClassName: styles.activityHistoryDropdownBackdrop
                    }
                }}
            />
        </div>
    );
};

export default ActivityHistoryDrawer;
